import { Icon, Tooltip, Typography } from "@mui/material";
import { get } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Col, Row } from "reactstrap";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import { Container } from "src/components/Container/Container";
import CustomIcon, { IconName } from "src/components/Icon/Icon";
import DisTable from "src/components/TableV2/Table";
import { TableActions } from "src/components/TableV2/TableActions";
import Toast from "src/components/Toast/Toast";
import * as S from "src/constants/StringConstants";
import { IContract, IDocument } from "src/store/models/document.model";
import { IRootState } from "src/store/reducers";
import {
  ContractingModalData,
  deleteBrokerContract,
  deleteContractDocuments,
  downloadBrokerContract,
  fetchBrokerContractDetails,
  fetchBrokerContracts,
  fetchBrokerContractsOptions,
  fetchContractDocuments,
  toggleBrokerUploadToast,
  uploadBrokerContract,
} from "src/store/reducers/brokerContracts";
import { saveToDownloads } from "src/util/util.fileService";
import { iso8601ToShort } from "src/util/utils.dates";
import { ContractingDocumentsModal } from "./ContractingDocumentsModal";
import ContractingModal from "./ContractingModal";

const BrokerContracts = () => {
  const carrierTableRef = useRef<any>(null);
  const [item, setItem] = useState<ContractingModalData | null>(null);
  const [uploadModal, toggleUploadModal] = useState(false);
  const [documentsModal, toggleDocumentsModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contractToDelete, setContractToDelete] = useState<IContract | null>(null);
  const { id: brokerID } = useParams<{ id: string }>();
  const dispatch = useDispatch();

  const {
    isUploadToastVisible,
    inProcess,
    contractDocuments,
    contractDetails,
    options: { selectCarrierList, selectStatusTypeList },
  } = useSelector((state: IRootState) => state.brokerContracts);

  useEffect(() => {
    dispatch(fetchBrokerContractsOptions(brokerID));
  }, []);

  const handleOpen = () => {
    dispatch(fetchBrokerContractsOptions(brokerID));
    toggleUploadModal(true);
  };

  const handleSave = async (data: ContractingModalData, form) => {
    try {
      await dispatch(uploadBrokerContract(brokerID, data));
      carrierTableRef.current?.update({});
      toggleUploadModal(false);
      form.reset();
    } catch (e) {
      alert(S.BRO_DOC_UPLOAD_ERROR);
    } finally {
    }
  };

  const handleDeleteContract = async (contract: IContract) => {
    setContractToDelete(contract);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (contractToDelete) {
      try {
        await dispatch(deleteBrokerContract(brokerID, `${contractToDelete.id}`));
        carrierTableRef.current?.update({});
      } catch (e) {
        alert(e);
      } finally {
        setIsDeleteModalOpen(false);
        setContractToDelete(null);
      }
    }
  };

  const handleDeleteContractDocs = async (documentIds: number[]) => {
    if (!contractDetails.id) return;
    try {
      await dispatch(deleteContractDocuments(brokerID, contractDetails.id, documentIds));
      dispatch(fetchBrokerContractDetails(brokerID, contractDetails.id));
      dispatch(fetchContractDocuments(brokerID, contractDetails.id));
      carrierTableRef.current?.update({});
    } catch (e) {
      alert(S.BRO_DOC_DELETE_ERROR);
    }
  };

  const handleDownload = async (documentId: number) => {
    try {
      const result = await dispatch(downloadBrokerContract(brokerID, `${documentId}`));
      const fileName = get(result, "value.headers.x-attachment-filename");
      const data = get(result, "value.data");
      if (fileName && data) {
        saveToDownloads(fileName, "application/pdf", data);
      }
    } catch (e) {
      alert(e);
    }
  };

  const carrierColumns = [
    {
      key: "carrierTypeID",
      title: S.BRO_CONTRACTING_CARRIER,
      render: item => <>{selectCarrierList.find(c => c.value === item.carrierTypeID)?.key}</>,
    },
    {
      key: "statusTypeID",
      title: S.BRO_CONTRACTING_STATUS,
      render: item => <>{selectStatusTypeList.find(c => c.value === item.statusTypeID)?.key}</>,
    },
    { key: "licenseNumber", title: S.BRO_CONTRACTING_LICENSE },
    {
      key: "expirationDate",
      title: S.BRO_CONTRACTING_EXPIRATION_DATE,
      render: item => iso8601ToShort(item.expirationDate),
    },
    {
      key: "modifiedDate",
      title: S.BRO_DOC_MODIFIED_DATE_COL,
      render: item => (
        <Tooltip title={item.modifiedBy}>
          <span>{iso8601ToShort(item.modifiedDate)}</span>
        </Tooltip>
      ),
    },
    {
      key: "notes",
      title: S.BRO_DOC_NOTES_COL,
      render: item =>
        item.notes && (
          <Tooltip title={item.notes}>
            <Icon>info</Icon>
          </Tooltip>
        ),
    },
    {
      key: "noOfDocuments",
      title: S.BRO_DOC_DOCUMENTS_COL,
    },
    {
      key: "actions",
      title: S.BRO_COL_ACTIONS,
      render: item =>
        item.deleted ? (
          <span>{S.BRO_DELETED}</span>
        ) : (
          <TableActions
            actions={[
              {
                label: S.BRO_DOC_VIEW_DOC_BUTTON,
                handler: () => {
                  dispatch(fetchContractDocuments(brokerID, item.id));
                  dispatch(fetchBrokerContractDetails(brokerID, item.id));
                  toggleDocumentsModal(true);
                },
              },
              {
                label: S.BRO_DOC_EDIT_BUTTON,
                handler: () => {
                  setItem(item);
                  handleOpen();
                },
              },
              { label: S.BRO_DELETE, handler: () => handleDeleteContract(item) },
            ]}
          />
        ),
    },
  ];

  return (
    <>
      <main className="broker-tab__wrap">
        <section className="broker-tab__container">
          <Container>
            <div className="broker-tab__form-wrap">
              <Row>
                <Col sm="9" className="doc-tab__title">
                  <h1 className="form-title">{S.BRO_CONTRACTING_TITLE}</h1>
                </Col>
                <Col sm="3" align="right">
                  <button
                    className="button-v2 button-v2__primary broker-tab__action-button"
                    onClick={() => {
                      setItem(null);
                      handleOpen();
                    }}>
                    <CustomIcon name={IconName.Plus} />
                    {S.BRO_DOC_ADD_CARRIER_BUTTON}
                  </button>
                </Col>
              </Row>

              <Row>
                <Col className="doc-tab__table-override">
                  <DisTable<IDocument>
                    columns={carrierColumns}
                    isPaginated={false}
                    initialState={{}}
                    getRef={ft => {
                      carrierTableRef.current = ft;
                    }}
                    onUpdate={async () => {
                      const result = await dispatch(fetchBrokerContracts(brokerID));
                      return {
                        total: 0,
                        data: get(result, "value.data", []),
                      };
                    }}
                  />
                </Col>
              </Row>
            </div>
          </Container>
        </section>
      </main>

      <Form
        onSubmit={(values, form) => handleSave(values, form)}
        initialValues={item || {}}
        mutators={{
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
        }}
        validate={values => {
          const errors: any = {};
          if (!values.carrierTypeID) errors.carrierTypeID = S.FORM_FIELD_REQUIRED;
          if (values.carrierTypeID && !values.expirationDate) errors.expirationDate = S.FORM_FIELD_REQUIRED;
          if (!values.statusTypeID) errors.statusTypeID = S.FORM_FIELD_REQUIRED;
          return errors;
        }}>
        {({ handleSubmit, form, ...formProps }) => (
          <ContractingModal
            open={uploadModal}
            onClose={() => {
              toggleUploadModal(false);
            }}
            onConfirm={handleSubmit}
            isEdit={!!item}
            selectCarrierList={selectCarrierList}
            selectStatusTypeList={selectStatusTypeList}
            form={form}
            formProps={formProps}
          />
        )}
      </Form>

      <Toast open={inProcess} message={S.LOADING} onClose={() => {}} />
      <Toast
        open={isUploadToastVisible}
        message={S.BRO_DOC_FILE_UPLOAD_SUCCESS_TOAST}
        onClose={() => dispatch(toggleBrokerUploadToast(false))}
      />

      {!inProcess && (
        <ContractingDocumentsModal
          open={documentsModal}
          onClose={() => toggleDocumentsModal(false)}
          onSave={async (filesToUpload: File[]) => {
            for (const file of filesToUpload) {
              const payload: ContractingModalData = {
                ...contractDetails,
                file,
              };
              await dispatch(uploadBrokerContract(brokerID, payload));
              carrierTableRef.current?.update({});
            }
            dispatch(fetchContractDocuments(brokerID, contractDetails.id));
          }}
          existingDocuments={contractDocuments}
          onDownload={handleDownload}
          onDelete={handleDeleteContractDocs}
        />
      )}

      {isDeleteModalOpen && (
        <PromptModal
          open={isDeleteModalOpen}
          title={S.BRO_DELETE_CONFIRM_TITLE}
          confirmText={S.BRO_DELETE_CONFIRM_CONFIRM}
          rejectText={S.BRO_DELETE_CONFIRM_REJECT}
          onConfirm={handleConfirmDelete}
          onReject={() => setIsDeleteModalOpen(false)}>
          <Typography variant="h6">{S.BRO_DOC_DELETE_CONFIRM_MESSAGE}</Typography>
        </PromptModal>
      )}
    </>
  );
};

export default BrokerContracts;
